import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import { FiChevronDown, FiChevronUp, FiRefreshCw, FiEdit3, FiSave, FiX } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL, API_URLS } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { generateSchedule, generateScheduleFromFirstRound, validateSchedule } from '../utilities/scheduleGenerator';
import Loader from 'react-loader-spinner';
import { useI18n } from '../utilities/i18n';
import { formatMatchDate, moscowTodayIso } from '../utilities/matchesApi';
import { kickoffFieldsForApi, resolveMatchTimezone } from '../utilities/matchKickoff';
import { useChampionships } from '../utilities/championships';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import {
  champFields,
  extractEntity,
  extractList as extractTournamentList,
  normalizeSeasonTeam,
} from '../utilities/tournamentsApi';
import { EMPTY_REFEREE_ID, extractAdminStaff, staffDisplayName } from '../utilities/matchStaffApi';
import { resetMobileViewport } from '../utilities/mobileViewport';
import '../styles/createMatchModal.css';
import '../styles/scheduleManagement.css';

const parsePositiveId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const UNASSIGNED_STADIUM_ID = 14;

const normalizeScheduleTeam = (item = {}) => {
  const team = normalizeSeasonTeam(item);
  if (!team.team_id) return null;
  return {
    ...item,
    ...team,
    id: team.team_id,
    team_id: team.team_id,
    team_name: team.name,
    name: team.name,
    logo: team.image || item.logo || item.image || '',
  };
};

const buildGroupsDataFromServer = (payload, fallbackLabel) => {
  const raw = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload?.groups)
      ? payload.groups
      : (Array.isArray(payload?.data) ? payload.data : []));
  if (!raw.length) return {};

  const looksStructured = Array.isArray(raw[0]?.teams)
    || ((raw[0]?.name || raw[0]?.group_name) && raw[0]?.team_id == null);

  const grouped = {};
  if (looksStructured) {
    raw.forEach((group) => {
      const gName = group.name || group.group_name || fallbackLabel;
      grouped[gName] = {
        teams: (group.teams || []).map(normalizeScheduleTeam).filter(Boolean),
      };
    });
    return grouped;
  }

  raw.forEach((row) => {
    const gName = row.group_name || row.name || fallbackLabel;
    if (!grouped[gName]) grouped[gName] = { teams: [] };
    const team = normalizeScheduleTeam(row);
    if (team) grouped[gName].teams.push(team);
  });
  return grouped;
};

function ScheduleManagement() {
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId, selected: championship, loading: champsLoading } = useChampionships();

  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);

  // Команды выбранной лиги
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [teamsLoadError, setTeamsLoadError] = useState('');

  // Групповые данные (для league_type = 2)
  const [leagueType, setLeagueType] = useState(1);
  const [groupsData, setGroupsData] = useState({}); // { 'А': { teams: [...] }, 'Б': { ... } }
  const [generatedGroupSchedules, setGeneratedGroupSchedules] = useState(null); // { 'А': tours[], 'Б': tours[] }

  // Параметры генерации
  const [generationMode, setGenerationMode] = useState('auto'); // 'auto' или 'manual'
  const [firstRoundMatches, setFirstRoundMatches] = useState([]); // [{home: null, away: null}, ...]
  const [groupFirstRoundMatches, setGroupFirstRoundMatches] = useState({}); // { 'А': [{home, away}, ...], 'Б': [...] }
  const [firstRoundDate, setFirstRoundDate] = useState(moscowTodayIso());
  const [roundsCount, setRoundsCount] = useState(1);
  const [halfTimeDuration, setHalfTimeDuration] = useState('30');
  const [selectedStadium, setSelectedStadium] = useState(UNASSIGNED_STADIUM_ID);
  const [selectedReferee, setSelectedReferee] = useState(EMPTY_REFEREE_ID);
  const [daysBetweenRounds, setDaysBetweenRounds] = useState('7');
  // Без дат — календарь только по турам: матчи создаются без даты, их потом ставят в «Играх».
  const [withDates, setWithDates] = useState(true);

  const stadiumCacheRef = useRef({});
  const refereeCacheRef = useRef({});
  const selectedStadiumRef = useRef(UNASSIGNED_STADIUM_ID);
  const selectedRefereeRef = useRef(EMPTY_REFEREE_ID);
  selectedStadiumRef.current = selectedStadium;
  selectedRefereeRef.current = selectedReferee;

  // Результат генерации
  const [generatedSchedule, setGeneratedSchedule] = useState(null);
  const [validation, setValidation] = useState(null);
  const [expandedTours, setExpandedTours] = useState({});

  // Загрузка
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [confirmSave, setConfirmSave] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null); // { text, type } — ответ в модалке сохранения
  const messageToastRef = useRef(null);

  const showFeedback = useCallback((text, type = 'error') => {
    const next = { text: String(text || '').trim(), type };
    if (!next.text) return;
    setMessage(next);
  }, []);

  useEffect(() => {
    if (!message.text || confirmSave) return undefined;
    const node = messageToastRef.current;
    if (node && typeof node.focus === 'function') {
      node.focus();
    }
    if (message.type !== 'success') return undefined;
    const timer = window.setTimeout(() => {
      setMessage((prev) => (prev.type === 'success' ? { text: '', type: '' } : prev));
    }, 8000);
    return () => window.clearTimeout(timer);
  }, [message.text, message.type, confirmSave]);

  useEffect(() => {
    let cancelled = false;

    const hydrateFromQuery = async (nextCountries) => {
      const params = new URLSearchParams(location.search || '');
      const countryId = parsePositiveId(params.get('country'));
      const leagueId = parsePositiveId(params.get('league'));
      const seasonId = parsePositiveId(params.get('season'));
      if (!countryId) return;

      const country = findByValue(nextCountries, countryId);
      if (!country) return;
      setSelectedCountry(country);

      const nextLeagues = await loadLeaguesForCountry(country.value);
      if (cancelled || !leagueId) return;

      const league = findByValue(nextLeagues, leagueId);
      if (!league) return;
      setSelectedLeague(league);

      const nextSeasons = await loadSeasonsForLeague(league);
      if (cancelled || !seasonId) return;

      const season = findByValue(nextSeasons, seasonId);
      if (season) {
        setSelectedSeason(season);
      }
    };

    const initCascade = async () => {
      resetCascade();
      if (!champId) {
        setCountries([]);
        return;
      }
      const nextCountries = await loadCountries();
      if (!cancelled) {
        await hydrateFromQuery(nextCountries);
      }
    };

    initCascade();
    return () => {
      cancelled = true;
    };
  }, [champId, location.search]);

  useEffect(() => {
    if (selectedLeague && selectedSeason && !champsLoading) {
      loadLeagueTeams();
    } else if (!selectedLeague || !selectedSeason) {
      setTeams([]);
      setGroupsData({});
      setTeamsLoadError('');
    }
  }, [selectedLeague, selectedSeason, champId, champsLoading]);

  useEffect(() => {
    if (!selectedLeague || !selectedSeason || loadingTeams) return undefined;
    const node = document.getElementById('sch-params-section');
    if (!node) return undefined;
    const timer = window.setTimeout(() => {
      node.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [selectedLeague, selectedSeason, loadingTeams, teams.length, groupsData]);

  useEffect(() => {
    if (!selectedLeague || !selectedSeason) {
      setLeagueType(1);
    }
  }, [selectedLeague, selectedSeason]);

  // Инициализируем слоты первого тура при изменении команд
  // Для нечётного числа команд: одна пропускает, поэтому (N-1)/2 матчей
  useEffect(() => {
    if (teams.length >= 2) {
      const numMatches = Math.floor(teams.length / 2);
      const emptyMatches = Array.from({ length: numMatches }, () => ({ home: null, away: null }));
      setFirstRoundMatches(emptyMatches);
    }
  }, [teams]);

  // Инициализируем слоты первого тура для каждой группы
  useEffect(() => {
    if (leagueType === 2 && Object.keys(groupsData).length > 0) {
      const groupMatches = {};
      Object.entries(groupsData).forEach(([groupName, group]) => {
        if (group.teams.length >= 2) {
          const numMatches = Math.floor(group.teams.length / 2);
          groupMatches[groupName] = Array.from({ length: numMatches }, () => ({ home: null, away: null }));
        }
      });
      setGroupFirstRoundMatches(groupMatches);
    }
  }, [groupsData, leagueType]);

  const findByValue = (items, value) => {
    return (items || []).find((item) => String(item.value) === String(value));
  };

  const resetGenerated = () => {
    setTeams([]);
    setGeneratedSchedule(null);
    setGeneratedGroupSchedules(null);
    setGroupsData({});
  };

  const resetCascade = () => {
    setSelectedCountry(null);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setLeagues([]);
    setSeasons([]);
    resetGenerated();
    setMessage({ text: '', type: '' });
  };

  const mapSelectOptions = (items, getValue, getName) => (
    items
      .map((item, index) => {
        const value = Number(getValue(item));
        return {
          ...item,
          value,
          name: getName(item) || '',
          key: index,
        };
      })
      .filter((item) => item.value > 0)
  );

  const mapSeasons = (items) => mapSelectOptions(
    extractTournamentList(items),
    (item) => item.season_id || item.id,
    (item) => item.season_name || item.name
  );

  const loadCountries = async () => {
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId));
      const nextCountries = mapSelectOptions(
        extractTournamentList(response.data),
        (item) => item.id,
        (item) => item.name
      );
      setCountries(nextCountries);
      return nextCountries;
    } catch (error) {
      console.error('Error loading countries:', error);
      setCountries([]);
      setMessage({ text: t('tsLoadCountriesError'), type: 'error' });
      return [];
    }
  };

  const loadLeaguesForCountry = async (countryId) => {
    if (!countryId) {
      setLeagues([]);
      return [];
    }

    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(countryId) });
      const nextLeagues = mapSelectOptions(
        extractTournamentList(extractEntity(response.data).leagues),
        (item) => item.id || item.league_id,
        (item) => item.name || item.league_name
      ).map((item) => ({
        ...item,
        seasons: extractTournamentList(item.seasons),
      }));
      setLeagues(nextLeagues);
      return nextLeagues;
    } catch (error) {
      console.error('Error loading leagues:', error);
      setLeagues([]);
      setMessage({ text: t('tsLoadLeaguesError'), type: 'error' });
      return [];
    }
  };

  const loadSeasonsForLeague = async (league) => {
    if (!league) {
      setSeasons([]);
      return [];
    }

    let nextSeasons = mapSeasons(league.seasons);
    if (!nextSeasons.length) {
      try {
        const response = await authorizedPost(dispatch, API_URLS.getAdminLeague, { id: Number(league.value) });
        nextSeasons = mapSeasons(extractEntity(response.data).seasons);
      } catch (error) {
        console.error('Error loading seasons:', error);
        nextSeasons = [];
        setMessage({ text: t('tsLoadSeasonsError'), type: 'error' });
      }
    }

    setSeasons(nextSeasons);
    return nextSeasons;
  };

  const loadLeagueTeams = async () => {
    if (!selectedLeague || !selectedSeason) return;

    setLoadingTeams(true);
    setTeamsLoadError('');
    try {
      let type = Number(selectedLeague.league_type) || 1;
      try {
        const seasonResponse = await authorizedPost(dispatch, API_URLS.getAdminSeason, {
          id: Number(selectedSeason.value),
          ...champFields(champId),
        });
        const settings = extractEntity(seasonResponse.data).league_settings || {};
        type = Number(settings.league_type) || type;
      } catch (seasonError) {
        console.error('Error loading season settings:', seasonError);
      }
      setLeagueType(type);

      // POST /leagues/:id/teamsByGroups — явные 400 с err/code (NEED_TEAMS, NEED_GROUP_TEAMS, …)
      const teamsByGroupsUrl = API_URLS.getTeamsByGroups.replace(':leagueId', selectedLeague.value);
      const response = await authorizedPost(dispatch, teamsByGroupsUrl, {
        ...champFields(champId),
        season_id: Number(selectedSeason.value),
      });
      const list = Array.isArray(response.data)
        ? response.data
        : (Array.isArray(response.data?.data) ? response.data.data : []);

      if (type === 2) {
        const grouped = buildGroupsDataFromServer(list, t('schUngrouped'));
        setGroupsData(grouped);
        const flat = Object.values(grouped).reduce((acc, group) => acc.concat(group.teams || []), []);
        setTeams(flat);
      } else {
        setGroupsData({});
        setTeams(list.map(normalizeScheduleTeam).filter(Boolean));
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      // Показывать серверный err/error, не подменять одной клиентской фразой
      const errorText = getApiErrorMessage(error, t('schLoadTeamsError'));
      setTeams([]);
      setGroupsData({});
      setTeamsLoadError(errorText);
      setMessage({ text: errorText, type: 'error' });
    } finally {
      setLoadingTeams(false);
    }
  };

  const handleCountryChange = async (countryId) => {
    const country = countryId ? findByValue(countries, countryId) : null;
    setSelectedCountry(country || null);
    setSelectedLeague(null);
    setSelectedSeason(null);
    setLeagues([]);
    setSeasons([]);
    resetGenerated();
    setTeamsLoadError('');
    setMessage({ text: '', type: '' });
    await loadLeaguesForCountry(country ? country.value : null);
    resetMobileViewport();
  };

  const handleLeagueChange = async (leagueId) => {
    const league = leagueId ? findByValue(leagues, leagueId) : null;
    setSelectedLeague(league || null);
    setSelectedSeason(null);
    setSeasons([]);
    resetGenerated();
    setTeamsLoadError('');
    setMessage({ text: '', type: '' });
    await loadSeasonsForLeague(league);
    resetMobileViewport();
  };

  const handleSeasonChange = (seasonId) => {
    const season = findByValue(seasons, seasonId);
    setSelectedSeason(season || null);
    setGeneratedSchedule(null);
    setGeneratedGroupSchedules(null);
    setTeamsLoadError('');
    setMessage({ text: '', type: '' });
    resetMobileViewport();
  };

  const unassignedStadiumOption = useMemo(
    () => ({ value: UNASSIGNED_STADIUM_ID, name: t('schRefereeNone'), key: 'unassigned-stadium' }),
    [t]
  );

  const unassignedRefereeOption = useMemo(
    () => ({ value: EMPTY_REFEREE_ID, name: t('schRefereeNone'), key: 'unassigned-referee' }),
    [t]
  );

  const defaultStadiumOptions = useMemo(() => [unassignedStadiumOption], [unassignedStadiumOption]);
  const defaultRefereeOptions = useMemo(() => [unassignedRefereeOption], [unassignedRefereeOption]);

  const rememberOptions = (cacheRef, list) => {
    list.forEach((item) => {
      if (item && item.value != null) {
        cacheRef.current[item.value] = item;
      }
    });
  };

  const mergeSelectedOption = (list, cacheRef, selectedValue) => {
    const selected = cacheRef.current[selectedValue];
    if (selected && !list.some((item) => String(item.value) === String(selected.value))) {
      return [selected, ...list];
    }
    return list;
  };

  const getStadiumOptions = useCallback(async (query = '') => {
    if (!champId) {
      return [unassignedStadiumOption];
    }

    try {
      const body = champFields(champId);
      const search = String(query || '').trim();
      if (search) body.search = search;
      const response = await authorizedPost(dispatch, API_URLS.getAllAdminStadiums, body);
      const list = (Array.isArray(response.data) ? response.data : (response.data?.data || []))
        .map((item, index) => {
          const value = Number(item.id || item.stadium_id);
          if (!Number.isFinite(value) || value <= 0) return null;
          return {
            ...item,
            value,
            name: item.name || item.stadium_name || t('schStadiumId', { id: value }),
            address: item.address || '',
            key: index,
          };
        })
        .filter(Boolean);
      const hasUnassigned = list.some((item) => Number(item.value) === UNASSIGNED_STADIUM_ID);
      const next = hasUnassigned ? list : [unassignedStadiumOption, ...list];
      rememberOptions(stadiumCacheRef, next);
      return mergeSelectedOption(next, stadiumCacheRef, selectedStadiumRef.current);
    } catch (error) {
      console.error('Error loading stadiums:', error);
      return [unassignedStadiumOption];
    }
  }, [champId, dispatch, t, unassignedStadiumOption]);

  const getRefereeOptions = useCallback(async (query = '') => {
    if (!champId) {
      return [unassignedRefereeOption];
    }

    try {
      const body = { ...champFields(champId), role: 13, with_games: false };
      const search = String(query || '').trim();
      if (search) body.search = search;
      const response = await authorizedPost(dispatch, API_URLS.getAdminStaff, body);
      const list = extractAdminStaff(response.data)
        .map((item, index) => {
          const value = Number(item.id || item.user_id);
          if (!Number.isFinite(value) || value <= 0 || value === EMPTY_REFEREE_ID) return null;
          return {
            ...item,
            value,
            name: staffDisplayName(item) || t('schRefereeId', { id: value }),
            login: item.login || '',
            key: index,
          };
        })
        .filter(Boolean);
      const next = [unassignedRefereeOption, ...list];
      rememberOptions(refereeCacheRef, next);
      return mergeSelectedOption(next, refereeCacheRef, selectedRefereeRef.current);
    } catch (error) {
      console.error('Error loading referees:', error);
      return [unassignedRefereeOption];
    }
  }, [champId, dispatch, t, unassignedRefereeOption]);

  // --- Первый тур (ручной режим) ---
  const getUsedTeamIds = (excludeIndex = -1) => {
    const ids = new Set();
    firstRoundMatches.forEach((match, idx) => {
      if (idx === excludeIndex) return;
      if (match.home) ids.add(match.home.team_id || match.home.id);
      if (match.away) ids.add(match.away.team_id || match.away.id);
    });
    return ids;
  };

  const getAvailableTeamsForSlot = (matchIndex, side) => {
    const usedIds = getUsedTeamIds(matchIndex);
    // Убираем команду, уже выбранную на другой стороне этого же матча
    const currentMatch = firstRoundMatches[matchIndex];
    const otherSide = side === 'home' ? currentMatch.away : currentMatch.home;
    if (otherSide) {
      usedIds.add(otherSide.team_id || otherSide.id);
    }
    // Не убираем текущий выбор этой стороны (чтобы он отображался в списке)
    const currentTeam = side === 'home' ? currentMatch.home : currentMatch.away;
    if (currentTeam) {
      usedIds.delete(currentTeam.team_id || currentTeam.id);
    }

    return teams.filter(t => !usedIds.has(t.team_id || t.id));
  };

  const handleFirstRoundTeamChange = (matchIndex, side, teamId) => {
    const team = teams.find(t => (t.team_id || t.id) === teamId) || null;
    setFirstRoundMatches(prev => {
      const updated = [...prev];
      updated[matchIndex] = { ...updated[matchIndex], [side]: team };
      return updated;
    });
    setGeneratedSchedule(null);
  };

  const isFirstRoundComplete = () => {
    return firstRoundMatches.length > 0 && firstRoundMatches.every(m => m.home && m.away);
  };

  const handleModeChange = (mode) => {
    setGenerationMode(mode);
    setGeneratedSchedule(null);
    setGeneratedGroupSchedules(null);
  };

  // --- Логика первого тура для групп ---
  const getGroupUsedTeamIds = (groupName, excludeIndex = -1) => {
    const ids = new Set();
    const matches = groupFirstRoundMatches[groupName] || [];
    matches.forEach((match, idx) => {
      if (idx === excludeIndex) return;
      if (match.home) ids.add(match.home.team_id || match.home.id);
      if (match.away) ids.add(match.away.team_id || match.away.id);
    });
    return ids;
  };

  const getGroupAvailableTeamsForSlot = (groupName, matchIndex, side) => {
    const groupTeams = groupsData[groupName]?.teams || [];
    const usedIds = getGroupUsedTeamIds(groupName, matchIndex);
    const matches = groupFirstRoundMatches[groupName] || [];
    const currentMatch = matches[matchIndex];
    if (currentMatch) {
      const otherSide = side === 'home' ? currentMatch.away : currentMatch.home;
      if (otherSide) usedIds.add(otherSide.team_id || otherSide.id);
      const currentTeam = side === 'home' ? currentMatch.home : currentMatch.away;
      if (currentTeam) usedIds.delete(currentTeam.team_id || currentTeam.id);
    }
    return groupTeams.filter(t => !usedIds.has(t.team_id || t.id));
  };

  const handleGroupFirstRoundTeamChange = (groupName, matchIndex, side, teamId) => {
    const groupTeams = groupsData[groupName]?.teams || [];
    const team = groupTeams.find(t => (t.team_id || t.id) === teamId) || null;
    setGroupFirstRoundMatches(prev => {
      const updated = { ...prev };
      const matches = [...(updated[groupName] || [])];
      matches[matchIndex] = { ...matches[matchIndex], [side]: team };
      updated[groupName] = matches;
      return updated;
    });
    setGeneratedGroupSchedules(null);
  };

  const isGroupFirstRoundComplete = () => {
    const groupNames = Object.keys(groupsData);
    if (groupNames.length === 0) return false;
    return groupNames.every(gName => {
      const matches = groupFirstRoundMatches[gName] || [];
      return matches.length > 0 && matches.every(m => m.home && m.away);
    });
  };

  const handleGenerate = () => {
    setMessage({ text: '', type: '' });
    if (leagueType !== 2 && generationMode === 'manual' && !isFirstRoundComplete()) {
      setMessage({ text: t('schFillFirst'), type: 'error' });
      return;
    }

    const halfTimeValue = Number(String(halfTimeDuration).trim());
    if (!String(halfTimeDuration).trim() || !Number.isFinite(halfTimeValue) || halfTimeValue <= 0) {
      setMessage({ text: t('schNeedHalfTime'), type: 'error' });
      return;
    }

    let daysBetweenValue = Number(String(daysBetweenRounds).trim());
    if (withDates) {
      if (!String(daysBetweenRounds).trim() || !Number.isFinite(daysBetweenValue) || daysBetweenValue <= 0) {
        setMessage({ text: t('schNeedDaysBetween'), type: 'error' });
        return;
      }
    } else {
      daysBetweenValue = Number.isFinite(daysBetweenValue) && daysBetweenValue > 0 ? daysBetweenValue : 7;
    }

    const currentStadium = stadiumCacheRef.current[selectedStadium];
    const currentReferee = refereeCacheRef.current[selectedReferee];
    const refFullName = currentReferee && Number(currentReferee.value) !== EMPTY_REFEREE_ID
      ? (currentReferee.name || staffDisplayName(currentReferee) || '')
      : '';

    const genOptions = {
      firstRoundDate,
      rounds: roundsCount,
      halfTimeDuration: halfTimeValue,
      stadiumId: selectedStadium,
      stadiumName: currentStadium?.name || currentStadium?.stadium_name || t('schRefereeNone'),
      refereeId: selectedReferee,
      refereeName: refFullName,
      daysBetweenRounds: daysBetweenValue,
      withDates,
    };

    if (leagueType === 2) {
      // Генерация для групп
      if (teamsLoadError) {
        setMessage({ text: teamsLoadError, type: 'error' });
        return;
      }
      const groupNames = Object.keys(groupsData);
      if (groupNames.length === 0) {
        setMessage({ text: t('schNoGroups'), type: 'error' });
        return;
      }
      if (generationMode === 'manual' && !isGroupFirstRoundComplete()) {
        setMessage({ text: t('schFillFirstGroups'), type: 'error' });
        return;
      }
      const groupSchedules = {};
      const expanded = {};
      groupNames.forEach(groupName => {
        const groupTeams = groupsData[groupName].teams;
        if (groupTeams.length < 2) return;
        let schedule;
        if (generationMode === 'manual') {
          schedule = generateScheduleFromFirstRound(groupFirstRoundMatches[groupName], genOptions, groupTeams);
        } else {
          schedule = generateSchedule(groupTeams, genOptions);
        }
        groupSchedules[groupName] = schedule;
        schedule.forEach(tour => {
          expanded[`${groupName}_${tour.tour_number}`] = true;
        });
      });
      setGeneratedGroupSchedules(groupSchedules);
      setGeneratedSchedule(null);
      setExpandedTours(expanded);
    } else {
      // Генерация для обычной лиги (league_type = 1)
      if (teamsLoadError) {
        setMessage({ text: teamsLoadError, type: 'error' });
        return;
      }
      if (generationMode === 'auto' && (!teams || teams.length < 2)) {
        setMessage({ text: t('schNeedTeams'), type: 'error' });
        return;
      }

      let schedule;
      if (generationMode === 'manual') {
        schedule = generateScheduleFromFirstRound(firstRoundMatches, genOptions, teams);
      } else {
        schedule = generateSchedule(teams, genOptions);
      }

      const result = validateSchedule(schedule);
      setValidation(result);
      setGeneratedSchedule(schedule);
      setGeneratedGroupSchedules(null);

      const expanded = {};
      schedule.forEach(tour => {
        expanded[tour.tour_number] = true;
      });
      setExpandedTours(expanded);
    }
  };

  const toggleTour = (tourNumber) => {
    setExpandedTours(prev => ({
      ...prev,
      [tourNumber]: !prev[tourNumber]
    }));
  };

  const collapseAll = () => {
    setExpandedTours({});
  };

  const expandAll = () => {
    const expanded = {};
    if (generatedGroupSchedules) {
      Object.entries(generatedGroupSchedules).forEach(([groupName, tours]) => {
        tours.forEach(tour => {
          expanded[`${groupName}_${tour.tour_number}`] = true;
        });
      });
    } else if (generatedSchedule) {
      generatedSchedule.forEach(tour => {
        expanded[tour.tour_number] = true;
      });
    }
    setExpandedTours(expanded);
  };

  const handleSaveSchedule = () => {
    const hasSchedule = generatedSchedule || generatedGroupSchedules;
    if (!hasSchedule || !selectedLeague || !selectedSeason) return;
    setSaveFeedback(null);
    setConfirmSave(true);
  };

  const closeSaveModal = () => {
    if (saving) return;
    setConfirmSave(false);
    setSaveFeedback(null);
  };

  const confirmSaveSchedule = async () => {
    setSaving(true);
    setSaveFeedback(null);
    setMessage({ text: '', type: '' });
    try {
      const isTokenExpired = checkIfTokenExpired();
      if (isTokenExpired) {
        await dispatch(refreshTokens());
      }
      const tokens = getTokensFromStore();

      if (generatedGroupSchedules) {
        // Сохранение для групп — объединяем все группы в единые туры
        const groupNames = Object.keys(generatedGroupSchedules);
        // Определяем максимальное количество туров среди всех групп
        const maxTours = Math.max(...groupNames.map(g => generatedGroupSchedules[g].length));

        const stages = [];
        for (let i = 1; i <= maxTours; i++) {
          stages.push({
            stage_name: i + ' Тур',
            short_stage_name: i + ' Тур',
            priority: i,
          });
        }

        // Собираем матчи всех групп, привязывая к единым турам
        const matches = [];
        for (const groupName of groupNames) {
          const tours = generatedGroupSchedules[groupName];
          tours.forEach(tour => {
            tour.matches.forEach(match => {
              const stadiumObj = stadiumCacheRef.current[match.stadium_id]
                || stadiumCacheRef.current[selectedStadium]
                || null;
              const matchTimezone = resolveMatchTimezone(
                stadiumObj,
                selectedLeague,
                selectedCountry,
                championship,
              );
              const kickoff = kickoffFieldsForApi(match.date, match.time, matchTimezone);
              matches.push({
                home_team_id: match.home_team.team_id || match.home_team.id,
                guest_team_id: match.away_team.team_id || match.away_team.id,
                date: kickoff.date,
                time: kickoff.time,
                timezone: matchTimezone,
                stadium_id: Number(match.stadium_id) || UNASSIGNED_STADIUM_ID,
                judje_id: match.referee_id || 2,
                half_time: match.half_time_duration,
                tour_number: match.tour_number,
              });
            });
          });
        }

        const response = await axios.post(
          BASE_URL.url + API_URLS.saveSchedule,
          {
            league_id: selectedLeague.value,
            season_id: selectedSeason.value,
            stages,
            matches,
            group_name: groupNames.join(','),
          },
          {
            headers: {
              Authorization: "Bearer " + tokens.access_token,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.data && response.data.answer) {
          const ok = { text: t('schSaved'), type: 'success' };
          setSaveFeedback(ok);
          showFeedback(ok.text, ok.type);
        } else {
          const failText = response.data?.err || response.data?.error || t('unknownError');
          const fail = { text: t('schSaveError', { error: failText }), type: 'error' };
          setSaveFeedback(fail);
          showFeedback(fail.text, fail.type);
        }
      } else {
        // Сохранение для обычной лиги
        const stages = generatedSchedule.map(tour => ({
          stage_name: tour.tour_number + ' Тур',
          short_stage_name: tour.tour_number + ' Тур',
          priority: tour.tour_number,
        }));

        const matches = generatedSchedule.flatMap(tour =>
          tour.matches.map(match => {
            const stadiumObj = stadiumCacheRef.current[match.stadium_id]
              || stadiumCacheRef.current[selectedStadium]
              || null;
            const matchTimezone = resolveMatchTimezone(
              stadiumObj,
              selectedLeague,
              selectedCountry,
              championship,
            );
            const kickoff = kickoffFieldsForApi(match.date, match.time, matchTimezone);
            return {
              home_team_id: match.home_team.team_id || match.home_team.id,
              guest_team_id: match.away_team.team_id || match.away_team.id,
              date: kickoff.date,
              time: kickoff.time,
              timezone: matchTimezone,
              stadium_id: Number(match.stadium_id) || UNASSIGNED_STADIUM_ID,
              judje_id: match.referee_id || 2,
              half_time: match.half_time_duration,
              tour_number: match.tour_number,
            };
          })
        );

        const response = await axios.post(
          BASE_URL.url + API_URLS.saveSchedule,
          {
            league_id: selectedLeague.value,
            season_id: selectedSeason.value,
            stages,
            matches,
          },
          {
            headers: {
              Authorization: "Bearer " + tokens.access_token,
              Accept: 'application/json',
              'Content-Type': 'application/json',
            }
          }
        );

        if (response.data && response.data.answer) {
          const ok = { text: t('schSaved'), type: 'success' };
          setSaveFeedback(ok);
          showFeedback(ok.text, ok.type);
        } else {
          const failText = response.data?.err || response.data?.error || t('unknownError');
          const fail = { text: t('schSaveError', { error: failText }), type: 'error' };
          setSaveFeedback(fail);
          showFeedback(fail.text, fail.type);
        }
      }
    } catch (error) {
      console.error('Error saving schedule:', error);
      const errMsg = getApiErrorMessage(error, t('unknownError'));
      const fail = { text: t('schSaveError', { error: errMsg }), type: 'error' };
      setSaveFeedback(fail);
      showFeedback(fail.text, fail.type);
    } finally {
      setSaving(false);
    }
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, {
      keys: ['name'],
      threshold: 0.3,
    });

    return (value) => {
      if (!value.length) {
        return options;
      }
      return fuse.search(value).map(item => item.item);
    };
  };

  const getTotalMatches = () => {
    if (generatedGroupSchedules) {
      return Object.values(generatedGroupSchedules).reduce((sum, tours) =>
        sum + tours.reduce((s, tour) => s + tour.matches.length, 0), 0);
    }
    if (!generatedSchedule) return 0;
    return generatedSchedule.reduce((sum, tour) => sum + tour.matches.length, 0);
  };

  const getTotalTours = () => {
    if (generatedGroupSchedules) {
      return Math.max(...Object.values(generatedGroupSchedules).map(tours => tours.length), 0);
    }
    if (!generatedSchedule) return 0;
    return generatedSchedule.length;
  };

  return (
    <div className="layoutWrapper">
      <div className="matchCenterWrapper sch-page">
        <div className="matchCenterHeader">
          <span className="matchCenterName">{t('navSchedule')}</span>
        </div>

        <div className="sch-content">
        {!champId && !champsLoading ? (
          <div className="sch-message error">{t('gmNeedChamp')}</div>
        ) : null}

        <div className="sch-section">
          <h2 className="sch-section-title">{t('schPickLeague')}</h2>
          <div className="sch-filters">
            <div className="sch-filter">
              <label>{t('gmCountry')}</label>
              <SelectSearch
                options={countries}
                search
                disabled={!champId}
                value={selectedCountry?.value}
                onChange={handleCountryChange}
                filterOptions={fuzzySearch}
                emptyMessage={t('cmCountryNotFound')}
                placeholder={t('cmCountryPh')}
              />
            </div>

            {selectedCountry ? (
              <div className="sch-filter">
                <label>{t('league')}</label>
                <SelectSearch
                  options={leagues}
                  search
                  value={selectedLeague?.value}
                  onChange={handleLeagueChange}
                  filterOptions={fuzzySearch}
                  emptyMessage={t('leagueNotFound')}
                  placeholder={t('leaguePlaceholder')}
                />
              </div>
            ) : null}

            {selectedLeague ? (
              <div className="sch-filter">
                <label>{t('stSeason')}</label>
                <SelectSearch
                  options={seasons}
                  search
                  value={selectedSeason?.value}
                  onChange={handleSeasonChange}
                  filterOptions={fuzzySearch}
                  emptyMessage={t('stSeasonNotFound')}
                  placeholder={t('stSeasonPlaceholder')}
                />
              </div>
            ) : null}
          </div>

          {selectedLeague && selectedSeason && (
            <div className="sch-teams-info">
              {loadingTeams ? (
                <div className="sch-teams-loading">
                  <Loader type="Oval" color="#3ad1a0" height={30} width={30} />
                  <span>{t('schLoadingTeams')}</span>
                </div>
              ) : leagueType === 2 && Object.keys(groupsData).length > 0 ? (
                <div className="sch-teams-count">
                  <div>{t('schLeagueType')}: <strong>{t('schGroupPlayoff')}</strong></div>
                  <div>{t('schGroups', { count: Object.keys(groupsData).length })}</div>
                  {Object.entries(groupsData).map(([groupName, group]) => (
                    <div key={groupName} className="sch-group-preview">
                      <span className="sch-group-label">{t('schGroup', { name: groupName })}</span>
                      {' '}
                      <span>{t('schGroupTeams', { count: group.teams.length })} </span>
                      <span className="sch-teams-preview">
                        {group.teams.map(teamItem => teamItem.team_name || teamItem.name).join(', ')}
                      </span>
                    </div>
                  ))}
                </div>
              ) : teamsLoadError ? (
                <div className="sch-teams-count sch-warning">{teamsLoadError}</div>
              ) : (
                <div className="sch-teams-count">
                  {t('schTeamsInLeague', { count: teams.length })}
                  {teams.length > 0 && (
                    <span className="sch-teams-preview">
                      {' '}({teams.map(teamItem => teamItem.team_name || teamItem.name).join(', ')})
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {selectedLeague && selectedSeason ? (
          <div className="sch-section sch-params-section" id="sch-params-section">
            <h2 className="sch-section-title">{t('schParams')}</h2>

            {loadingTeams ? (
              <div className="sch-teams-loading">
                <Loader type="Oval" color="#3ad1a0" height={30} width={30} />
                <span>{t('schLoadingTeams')}</span>
              </div>
            ) : teamsLoadError ? (
              <p className="sch-warning">{teamsLoadError}</p>
            ) : !(leagueType === 2 ? Object.keys(groupsData).length > 0 : teams.length >= 2) ? (
              <p className="sch-warning">{t('schNeedTeams')}</p>
            ) : (
              <>
            <div className="sch-mode">
              <button
                type="button"
                className={`sch-mode-btn ${generationMode === 'auto' ? 'is-active' : ''}`}
                onClick={() => handleModeChange('auto')}
              >
                {t('schAuto')}
              </button>
              <button
                type="button"
                className={`sch-mode-btn ${generationMode === 'manual' ? 'is-active' : ''}`}
                onClick={() => handleModeChange('manual')}
              >
                <FiEdit3 /> {t('schManual')}
              </button>
            </div>

            <label className="sch-with-dates">
              <input
                type="checkbox"
                checked={withDates}
                onChange={(e) => setWithDates(e.target.checked)}
              />
              <span>{t('schWithDates')}</span>
              {!withDates ? <span className="sch-with-dates-hint">{t('schWithoutDatesHint')}</span> : null}
            </label>

            <div className="sch-params">
              {withDates ? (
                <div className="sch-param">
                  <label>{t('schFirstDate')}</label>
                  <input
                    type="date"
                    value={firstRoundDate}
                    onChange={(e) => setFirstRoundDate(e.target.value)}
                    className="sch-input"
                  />
                </div>
              ) : null}

              <div className="sch-param">
                <label>{t('schRounds')}</label>
                <select
                  value={roundsCount}
                  onChange={(e) => setRoundsCount(Number(e.target.value))}
                  className="sch-input"
                >
                  <option value={1}>{t('schRound1')}</option>
                  <option value={2}>{t('schRound2')}</option>
                </select>
              </div>

              <div className="sch-param">
                <label>{t('schHalfTime')}</label>
                <input
                  type="number"
                  inputMode="numeric"
                  value={halfTimeDuration}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setHalfTimeDuration('');
                      return;
                    }
                    if (/^\d{1,3}$/.test(raw)) setHalfTimeDuration(raw);
                  }}
                  min={1}
                  max={90}
                  className="sch-input"
                />
              </div>

              {withDates ? (
                <div className="sch-param">
                  <label>{t('schDaysBetween')}</label>
                  <input
                    type="number"
                    inputMode="numeric"
                    value={daysBetweenRounds}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === '') {
                        setDaysBetweenRounds('');
                        return;
                      }
                      if (/^\d{1,3}$/.test(raw)) setDaysBetweenRounds(raw);
                    }}
                    min={1}
                    max={30}
                    className="sch-input"
                  />
                </div>
              ) : null}

              <div className="sch-param">
                <label>{t('stadium')}</label>
                <SelectSearch
                  options={defaultStadiumOptions}
                  search
                  disabled={!champId}
                  value={selectedStadium}
                  onChange={(value) => {
                    setSelectedStadium(value);
                    resetMobileViewport();
                  }}
                  getOptions={getStadiumOptions}
                  debounce={250}
                  emptyMessage={t('schStadiumNotFound')}
                  placeholder={t('schStadiumPlaceholder')}
                />
              </div>

              <div className="sch-param">
                <label>{t('schReferee')}</label>
                <SelectSearch
                  options={defaultRefereeOptions}
                  search
                  disabled={!champId}
                  value={selectedReferee}
                  onChange={(value) => {
                    setSelectedReferee(value);
                    resetMobileViewport();
                  }}
                  getOptions={getRefereeOptions}
                  debounce={250}
                  emptyMessage={t('schRefereeNotFound')}
                  placeholder={t('schRefereeNone')}
                />
              </div>
            </div>

            <div className="sch-generate-actions">
              <button
                type="button"
                className="sch-generate-btn"
                onClick={handleGenerate}
                disabled={generationMode === 'manual' && (leagueType === 2 ? !isGroupFirstRoundComplete() : !isFirstRoundComplete())}
              >
                <FiRefreshCw /> {t('schGenerate')}
              </button>
            </div>

            {leagueType === 2 && generationMode === 'manual' && (
              <div className="sch-first-round">
                <h3 className="sch-first-title">{t('schFirstRoundGroups')}</h3>
                {Object.entries(groupsData).map(([groupName]) => {
                  const matches = groupFirstRoundMatches[groupName] || [];
                  return (
                    <div key={groupName} className="sch-group-fr">
                      <h4 className="sch-group-title">{t('schGroup', { name: groupName })}</h4>
                      <div className="sch-first-matches">
                        {matches.map((match, idx) => {
                          const homeOptions = getGroupAvailableTeamsForSlot(groupName, idx, 'home').map(teamItem => ({
                            value: teamItem.team_id || teamItem.id,
                            name: teamItem.team_name || teamItem.name,
                          }));
                          const awayOptions = getGroupAvailableTeamsForSlot(groupName, idx, 'away').map(teamItem => ({
                            value: teamItem.team_id || teamItem.id,
                            name: teamItem.team_name || teamItem.name,
                          }));
                          return (
                            <div key={idx} className="sch-fr-row">
                              <div className="sch-fr-num">{idx + 1}</div>
                              <div className="sch-fr-team">
                                <SelectSearch
                                  options={homeOptions}
                                  search
                                  value={match.home ? (match.home.team_id || match.home.id) : ''}
                                  onChange={(val) => handleGroupFirstRoundTeamChange(groupName, idx, 'home', val)}
                                  filterOptions={fuzzySearch}
                                  emptyMessage={t('schNoAvailable')}
                                  placeholder={t('schHome')}
                                />
                              </div>
                              <span className="sch-vs">vs</span>
                              <div className="sch-fr-team">
                                <SelectSearch
                                  options={awayOptions}
                                  search
                                  value={match.away ? (match.away.team_id || match.away.id) : ''}
                                  onChange={(val) => handleGroupFirstRoundTeamChange(groupName, idx, 'away', val)}
                                  filterOptions={fuzzySearch}
                                  emptyMessage={t('schNoAvailable')}
                                  placeholder={t('schAway')}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {leagueType !== 2 && generationMode === 'manual' && (
              <div className="sch-first-round">
                <h3 className="sch-first-title">{t('schFirstRound')}</h3>
                <p className="sch-first-hint">
                  {t('schFirstRoundHint')}
                  {teams.length % 2 !== 0 && t('schByeHint')}
                </p>
                {teams.length % 2 !== 0 && isFirstRoundComplete() && (() => {
                  const usedIds = new Set();
                  firstRoundMatches.forEach(m => {
                    if (m.home) usedIds.add(m.home.team_id || m.home.id);
                    if (m.away) usedIds.add(m.away.team_id || m.away.id);
                  });
                  const byeTeam = teams.find(teamItem => !usedIds.has(teamItem.team_id || teamItem.id));
                  return byeTeam ? (
                    <div className="sch-bye">
                      {t('schByeTeam', { name: byeTeam.team_name || byeTeam.name })}
                    </div>
                  ) : null;
                })()}
                <div className="sch-first-matches">
                  {firstRoundMatches.map((match, idx) => {
                    const homeOptions = getAvailableTeamsForSlot(idx, 'home').map(teamItem => ({
                      value: teamItem.team_id || teamItem.id,
                      name: teamItem.team_name || teamItem.name,
                    }));
                    const awayOptions = getAvailableTeamsForSlot(idx, 'away').map(teamItem => ({
                      value: teamItem.team_id || teamItem.id,
                      name: teamItem.team_name || teamItem.name,
                    }));

                    return (
                      <div key={idx} className="sch-fr-row">
                        <div className="sch-fr-num">{idx + 1}</div>
                        <div className="sch-fr-team">
                          <SelectSearch
                            options={homeOptions}
                            search
                            value={match.home ? (match.home.team_id || match.home.id) : ''}
                            onChange={(val) => handleFirstRoundTeamChange(idx, 'home', val)}
                            filterOptions={fuzzySearch}
                            emptyMessage={t('schNoAvailable')}
                            placeholder={t('schHome')}
                          />
                        </div>
                        <span className="sch-vs">vs</span>
                        <div className="sch-fr-team">
                          <SelectSearch
                            options={awayOptions}
                            search
                            value={match.away ? (match.away.team_id || match.away.id) : ''}
                            onChange={(val) => handleFirstRoundTeamChange(idx, 'away', val)}
                            filterOptions={fuzzySearch}
                            emptyMessage={t('schNoAvailable')}
                            placeholder={t('schAway')}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {!isFirstRoundComplete() && (
                  <p className="sch-warning">{t('schFillAll')}</p>
                )}
              </div>
            )}
              </>
            )}
          </div>
        ) : null}

        {(generatedSchedule || generatedGroupSchedules) && (
          <div className="sch-section">
            <div className="sch-preview-header">
              <h2 className="sch-section-title">{t('schPreview')}</h2>
              <div className="sch-preview-stats">
                <span className="sch-stat">{t('schTours', { count: getTotalTours() })}</span>
                <span className="sch-stat">{t('schMatches', { count: getTotalMatches() })}</span>
                <span className="sch-stat">{t('schTeams', { count: teams.length })}</span>
                {generatedGroupSchedules && (
                  <span className="sch-stat">{t('schGroupsStat', { count: Object.keys(generatedGroupSchedules).length })}</span>
                )}
              </div>
            </div>

            <div className="sch-preview-actions">
              <button type="button" className="sch-small-btn" onClick={expandAll}>{t('schExpandAll')}</button>
              <button type="button" className="sch-small-btn" onClick={() => setExpandedTours({})}>{t('schCollapseAll')}</button>
              <button
                type="button"
                className="sch-save-btn"
                onClick={handleSaveSchedule}
                disabled={saving}
              >
                <FiSave /> {saving ? t('stSaving') : t('schSave')}
              </button>
            </div>
            {message.text ? (
              <div className={`sch-message sch-message-inline ${message.type}`}>{message.text}</div>
            ) : null}

            {generatedGroupSchedules && Object.entries(generatedGroupSchedules).map(([groupName, tours]) => (
              <div key={groupName} className="sch-group-block">
                <h3 className="sch-group-title">{t('schGroup', { name: groupName })}</h3>
                <div className="sch-tours">
                  {tours.map(tour => {
                    const tourKey = `${groupName}_${tour.tour_number}`;
                    return (
                      <div key={tourKey} className="sch-tour">
                        <div className="sch-tour-header" onClick={() => setExpandedTours(prev => ({ ...prev, [tourKey]: !prev[tourKey] }))}>
                          <div className="sch-tour-title">
                            <span className="sch-tour-number">{t('schTour', { n: tour.tour_number })}</span>
                            {roundsCount > 1 && <span className="sch-tour-circle">{t('schCircle', { n: tour.circle })}</span>}
                            {tour.date ? <span className="sch-tour-date">{formatMatchDate(tour.date)}</span> : null}
                            <span className="sch-tour-count">{t('schMatchCount', { count: tour.matches.length })}</span>
                          </div>
                          {expandedTours[tourKey] ? <FiChevronUp /> : <FiChevronDown />}
                        </div>

                        {expandedTours[tourKey] && (
                          <div className="sch-matches">
                            {tour.matches.map((match, idx) => (
                              <div key={match.id} className="sch-match">
                                <div className="sch-match-num">{idx + 1}</div>
                                <div className="sch-match-teams">
                                  <span className="sch-home">{match.home_team.team_name || match.home_team.name}</span>
                                  <span className="sch-vs">vs</span>
                                  <span className="sch-away">{match.away_team.team_name || match.away_team.name}</span>
                                </div>
                                <div className="sch-match-info">
                                  {match.time && <span>{match.time}</span>}
                                  <span className="sch-match-stadium">{match.stadium_name}</span>
                                  {match.referee_name && <span>{match.referee_name}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {generatedSchedule && (
            <div className="sch-tours">
              {generatedSchedule.map(tour => (
                <div key={tour.tour_number} className="sch-tour">
                  <div className="sch-tour-header" onClick={() => toggleTour(tour.tour_number)}>
                    <div className="sch-tour-title">
                      <span className="sch-tour-number">{t('schTour', { n: tour.tour_number })}</span>
                      {roundsCount > 1 && <span className="sch-tour-circle">{t('schCircle', { n: tour.circle })}</span>}
                      {tour.date ? <span className="sch-tour-date">{formatMatchDate(tour.date)}</span> : null}
                      <span className="sch-tour-count">{t('schMatchCount', { count: tour.matches.length })}</span>
                    </div>
                    {expandedTours[tour.tour_number] ? <FiChevronUp /> : <FiChevronDown />}
                  </div>

                  {expandedTours[tour.tour_number] && (
                    <div className="sch-matches">
                      {tour.matches.map((match, idx) => (
                        <div key={match.id} className="sch-match">
                          <div className="sch-match-num">{idx + 1}</div>
                          <div className="sch-match-teams">
                            <span className="sch-home">{match.home_team.team_name || match.home_team.name}</span>
                            <span className="sch-vs">vs</span>
                            <span className="sch-away">{match.away_team.team_name || match.away_team.name}</span>
                          </div>
                          <div className="sch-match-info">
                            {match.time && <span>{match.time}</span>}
                            <span className="sch-match-stadium">{match.stadium_name}</span>
                            {match.referee_name && <span>{match.referee_name}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            )}
          </div>
        )}
        </div>

        {confirmSave && (
          <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeSaveModal(); }}>
            <div className="modal-content sch-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{saveFeedback ? (saveFeedback.type === 'success' ? t('schSaved') : t('error')) : t('schSaveTitle')}</h2>
                <button type="button" className="modal-close" onClick={closeSaveModal} disabled={saving}>
                  <FiX />
                </button>
              </div>
              <div className="modal-body">
                <div className="sch-confirm-body">
                  {saveFeedback ? (
                    <p className={`sch-message sch-message-inline ${saveFeedback.type}`}>{saveFeedback.text}</p>
                  ) : (
                    <p>
                      {t('schSaveConfirm', {
                        tours: getTotalTours(),
                        matches: getTotalMatches(),
                        league: selectedLeague?.name || '',
                        groups: generatedGroupSchedules
                          ? t('schSaveGroups', { count: Object.keys(generatedGroupSchedules).length })
                          : '',
                      })}
                    </p>
                  )}
                  <div className="sch-confirm-actions">
                    {saveFeedback ? (
                      <button
                        type="button"
                        className="btn-modal btn-primary-modal"
                        onClick={closeSaveModal}
                      >
                        {t('close')}
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn-modal btn-secondary-modal"
                          onClick={closeSaveModal}
                          disabled={saving}
                        >
                          {t('cancel')}
                        </button>
                        <button
                          type="button"
                          className="btn-modal btn-primary-modal"
                          onClick={confirmSaveSchedule}
                          disabled={saving}
                        >
                          {saving ? t('stSaving') : t('save')}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {message.text && !confirmSave ? (
          <div
            ref={messageToastRef}
            className={`sch-toast ${message.type}`}
            role="alert"
            tabIndex={-1}
          >
            <span className="sch-toast-text">{message.text}</span>
            <button
              type="button"
              className="sch-toast-close"
              aria-label={t('close')}
              onClick={() => setMessage({ text: '', type: '' })}
            >
              <FiX />
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ScheduleManagement;
